import { type Announcements, DndContext, type DragEndEvent, KeyboardSensor, MouseSensor } from '@dnd-kit/core';
import { TouchSensor, closestCenter } from '@dnd-kit/core';
import { useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { cx } from '@vizit/theme';
import type * as React from 'react';

const DRAG_ACTIVATION_DISTANCE = 8;
const TOUCH_ACTIVATION_DELAY_MS = 250;
const TOUCH_ACTIVATION_TOLERANCE = 5;

interface SortableGridProps {
  items: string[];
  onReorder: (items: string[]) => void;
  renderItem: (id: string) => React.ReactNode;
  itemName: (id: string) => string;
  isDisabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

function SortableGridItem({
  id,
  name,
  isDisabled,
  children,
}: React.PropsWithChildren<{ id: string; name: string; isDisabled: boolean }>) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isDisabled,
    attributes: { role: 'group', roleDescription: 'sortable', tabIndex: isDisabled ? -1 : 0 },
  });
  const setRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    setActivatorNodeRef(node);
  };
  return (
    <div
      ref={setRef}
      style={{ transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, transition }}
      className={cx(
        'min-w-0 outline-none focus-visible:ring-2 focus-visible:ring-gray-modern-500 focus-visible:ring-offset-4',
        isDragging && 'relative z-10 opacity-80',
        !isDisabled && 'cursor-grab',
      )}
      {...attributes}
      {...listeners}
      aria-label={isDisabled ? name : `Reorder ${name}`}
      aria-roledescription={isDisabled ? undefined : attributes['aria-roledescription']}
      aria-describedby={isDisabled ? undefined : attributes['aria-describedby']}
      data-dragging={isDragging || undefined}
    >
      {children}
    </div>
  );
}

function SortableGrid(props: SortableGridProps) {
  const { items, onReorder, renderItem, itemName, isDisabled = false, className, style } = props;
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: TOUCH_ACTIVATION_DELAY_MS, tolerance: TOUCH_ACTIVATION_TOLERANCE },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const nameOf = (id: string | number) => itemName(String(id));
  const positionOf = (id: string | number) => `position ${items.indexOf(String(id)) + 1} of ${items.length}`;
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${nameOf(active.id)} at ${positionOf(active.id)}.`,
    onDragOver: ({ active, over }) => (over ? `${nameOf(active.id)} is over ${positionOf(over.id)}.` : undefined),
    onDragEnd: ({ active, over }) =>
      over ? `${nameOf(active.id)} dropped at ${positionOf(over.id)}.` : `${nameOf(active.id)} dropped.`,
    onDragCancel: ({ active }) => `Moving ${nameOf(active.id)} was cancelled.`,
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.indexOf(String(active.id));
    const to = items.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{ announcements }}
    >
      <SortableContext items={items} strategy={rectSortingStrategy} disabled={isDisabled}>
        <div className={className} style={style}>
          {items.map((id) => (
            <SortableGridItem key={id} id={id} name={itemName(id)} isDisabled={isDisabled}>
              {renderItem(id)}
            </SortableGridItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export { SortableGrid };
export type { SortableGridProps };
